import { Link } from 'wouter';
import { Wordmark } from '../components';
import { LEGAL_UPDATED } from './legalMeta';

/**
 * Legal and informational pages. Each page is a simple long-form document
 * with consistent typography and chrome — they're linked from the footer
 * and need to feel like proper standalone pages, not afterthoughts.
 *
 * The content here is plain-language placeholder copy that sets the right
 * tone for the brand. A real launch would replace this with attorney-
 * reviewed terms, but the structure and voice are correct for EntreDash.
 */

function LegalShell({ title, children }: { title: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="page">
      <div className="container" style={{ maxWidth: 760, paddingTop: 24, paddingBottom: 64 }}>
        <Link
          href="/"
          className="f-mono"
          style={{ fontSize: 12, color: 'var(--text-decorative)' }}
        >
          ← Home
        </Link>
        <h1
          className="f-display"
          style={{ fontSize: 'clamp(36px, 5vw, 48px)', margin: '20px 0 32px' }}
        >
          {title}
        </h1>
        <div className="legal-prose">{children}</div>
      </div>
      <style>{`
        .legal-prose { font-size: 16px; line-height: 1.7; color: var(--ink); }
        .legal-prose h2 {
          font-family: var(--f-serif);
          font-size: 22px;
          margin: 40px 0 14px;
          font-weight: 500;
        }
        .legal-prose h3 { font-family: var(--f-sans); font-size: 16px; font-weight: 600; margin: 24px 0 8px; }
        .legal-prose p { margin: 0 0 18px; }
        .legal-prose ul { margin: 0 0 18px; padding-left: 24px; }
        .legal-prose li { margin: 6px 0; }
        .legal-prose strong { font-weight: 600; }
        .legal-prose em { color: var(--text-decorative); }
        /* <address> is the semantically correct element for contact details,
           but browsers italicize it by default, which would make the postal
           address read as an aside rather than as a fact. */
        .legal-prose address { font-style: normal; margin: 0 0 18px; }
      `}</style>
    </div>
  );
}

export function AboutPage() {
  return (
    <LegalShell title={<>About <Wordmark size="inherit" /></>}>
      <p>
        <Wordmark size="inherit" /> is built for the everyday person with an idea. Not law firms.
        Not big-company R&amp;D teams. The ordinary person who thought of
        something useful and wants to know whether it is worth anything, and if
        so, how to build it into a real business.
      </p>
      <p>
        We start with the question most people skip: does anyone actually need
        this? From there: Is it patentable? What does the prior art say? How
        does it break into a buildable design? Can it support a real business?
        Each stage produces something you own: a strategy memo, a design
        breakdown, a five-year business case, a business model canvas. When you
        are ready to talk to an attorney, we connect you to a vetted one. The
        first idea is free, always. Education is free, always.
      </p>

      <h2>What we believe</h2>
      <ul>
        <li>The next great invention will come from someone who has never met a patent attorney.</li>
        <li>Most ideas are not worth pursuing. Saying so early, before time and money are spent, is the most useful thing we can do.</li>
        <li>A person with a raw idea deserves the same tools a well-funded startup takes for granted.</li>
        <li>Software cannot replace a real attorney. It can make the conversation with one productive.</li>
      </ul>

      <h2>What we are not</h2>
      <p>
        <Wordmark size="inherit" /> is not a law firm. We do not provide legal advice. We do not
        prepare or file patent applications. We do not represent inventors
        before the USPTO. Before taking any binding action on your idea,
        consult a licensed patent practitioner.
      </p>

      <h2>Get in touch</h2>
      <p>
        Press, partnerships, feedback, or just want to say hi:{' '}
        <a href="mailto:hello@entredash.com" style={{ color: 'var(--brass)' }}>
          hello@entredash.com
        </a>
        .
      </p>
    </LegalShell>
  );
}

export function ContactPage() {
  return (
    <LegalShell title="Contact">
      <p>
        We read every message. The fastest way to reach us is email — usually
        a same-day response from a real human.
      </p>

      <h2>General questions</h2>
      <p>
        <a href="mailto:hello@entredash.com" style={{ color: 'var(--brass)' }}>
          hello@entredash.com
        </a>
      </p>

      <h2>Support and account help</h2>
      <p>
        <a href="mailto:support@entredash.com" style={{ color: 'var(--brass)' }}>
          support@entredash.com
        </a>
      </p>

      <h2>Press and partnerships</h2>
      <p>
        <a href="mailto:press@entredash.com" style={{ color: 'var(--brass)' }}>
          press@entredash.com
        </a>
      </p>

      <h2>Feedback inside the app</h2>
      <p>
        Already signed in? You can submit feedback directly from{' '}
        <Link href="/settings/feedback" style={{ color: 'var(--brass)' }}>
          Settings → Feedback
        </Link>{' '}
        — it lands in the same inbox we read every morning.
      </p>

      {/* Postal address sits last on purpose: the page opens by saying email is
          the fastest way to reach us, so the channels run fastest-first. Kept
          in sync with MAILING_ADDRESS in the email footer — if one changes,
          change both. */}
      <h2>By mail</h2>
      <address>
        EntreDash
        <br />
        3723 Greenville Avenue STE 45690
        <br />
        Dallas, TX 75206
        <br />
        United States
      </address>
      <p>
        For legal notices and anything that needs a paper trail. If you want an
        answer, email is far faster.
      </p>
    </LegalShell>
  );
}

export function TermsBody() {
  // Legal notices go to `legal@entredash.com` throughout this document: DMCA
  // complaints, the pre-dispute contact, arbitration opt-outs, and the contact
  // block. Separated from `hello@` on purpose — a notice with a legal deadline
  // should not compete with general enquiries in one inbox, and a distinct
  // address is what makes "we received it on the 3rd" provable later.
  //
  // The mailbox is live and confirmed receiving (Aug 2026). It must STAY that
  // way: the arbitration opt-out below has a hard 30-day window, so if this
  // address ever bounces the company has published a right its customers cannot
  // exercise. Do not retire or re-point it without changing this document.
  //
  // `hello@` and `support@` remain the general and support channels; only legal
  // notices route here.
  return (
    <>
      <p>
        <em>Last updated: {LEGAL_UPDATED.terms}</em>
      </p>
      <p>
        These terms are an agreement between you and EntreDash, Inc.
        (&quot;<Wordmark size="inherit" />,&quot; &quot;we,&quot; &quot;us&quot;). By creating an
        account or using the service, you agree to them. We have tried to write
        them in plain English, and where a section carries real consequence we
        have said so rather than burying it.
      </p>
      <p>
        <strong>Two sections deserve your attention before the rest.</strong>{' '}
        &quot;What we are not&quot; explains that we are not your lawyer and
        nothing here is privileged. &quot;Disputes&quot; requires most
        disagreements to go to individual arbitration rather than court, and
        gives you 30 days to opt out of that.
      </p>

      <h2>What <Wordmark size="inherit" /> is</h2>
      <p>
        <Wordmark size="inherit" /> is an educational and strategic guidance
        product for inventors and founders. It runs AI-assisted advisory
        sessions, produces written strategy memos, searches public patent and
        market records, and teaches the vocabulary and process of turning an
        idea into a business.
      </p>

      <h2>What <Wordmark size="inherit" /> is not</h2>
      <p>
        <strong>We are not a law firm and we do not give legal advice.</strong>{' '}
        Nothing produced by the service is legal advice, and no part of using it
        creates an attorney-client relationship between you and us.
      </p>
      <p>
        <strong>Nothing you submit here is attorney-client privileged.</strong>{' '}
        Privilege protects communications with your own lawyer. It does not
        attach to what you type into this product. If you need privileged
        advice, speak to a licensed patent practitioner directly.
      </p>
      <p>
        We do not decide whether your idea is patentable. Only the USPTO, or
        another patent office, can do that. Our analysis is an informed starting
        point for a conversation with a professional, not a substitute for one.
      </p>

      <h2>Who can use it</h2>
      <ul>
        <li>
          If you are 18 or older, you may use every feature and buy any
          subscription or purchase.
        </li>
        <li>
          If you are 16 or 17, you may use EntreDash only with the consent of a
          parent or guardian who agrees to these terms on your behalf. You may
          buy a subscription or make a purchase only with that parent's or
          guardian's permission, using a payment method they have authorized.
          By making a purchase, you confirm you have that permission, and your
          parent or guardian is responsible for the purchase. We built
          EntreDash to be useful to student inventors, and this is the boundary
          that keeps that honest.
        </li>
        <li>
          Under 16, you may not use the service. (This minimum age rises from 13
          to 16 on October 11, 2026.)
        </li>
        <li>
          You must not be located in, or ordinarily resident in, a country
          subject to comprehensive United States sanctions, and you must not be
          on a United States restricted-party list.
        </li>
      </ul>

      <h2>Your account</h2>
      <ul>
        <li>You are responsible for the security of your account credentials.</li>
        <li>You are responsible for the accuracy of information you submit.</li>
        <li>
          One account is for one person. Do not share credentials or resell
          access.
        </li>
        <li>Tell us promptly if you believe your account has been compromised.</li>
      </ul>

      <h2>Your ideas are yours</h2>
      <p>
        Anything you submit, including ideas, descriptions, drawings and
        documents, belongs to you. So do the memos, canvases, and reports the service produces from
        them. We claim no ownership of any of it.
      </p>
      <p>
        <strong>
          We do not use your confidential idea content to train AI models.
        </strong>{' '}
        We do not sell it, publish it, or share it with third parties, except
        the service providers who operate the product for us and are bound to
        keep it confidential.
      </p>
      <p>
        You grant us only the narrow permission we need to run the service: to
        store your content, process it, and generate your deliverables from it.
        That permission ends when you delete the content or close your account.
      </p>

      <h2>Confidentiality, both ways</h2>
      <p>
        These terms form a binding mutual confidentiality agreement. We agree to
        keep confidential the ideas, descriptions, and materials you submit, and
        you agree to keep confidential any non-public information you learn
        through the service.
      </p>
      <p>
        Your content is held on encrypted servers in the United States and is
        disclosed to no third party without your explicit consent, except as
        described below.
      </p>
      <p>
        Like any confidentiality agreement, ours does not cover information
        that: is already public, or becomes public through no fault of ours;
        we already held before you disclosed it; we receive from someone else
        who was free to share it; or we develop independently without using your
        content. We may also disclose information where the law requires it, and
        we will tell you when we are permitted to.
      </p>
      <p>
        <strong>One thing this cannot do for you.</strong> Confidentiality here
        protects what you tell us. It cannot protect what you tell anyone else.
        Publishing, selling, or publicly describing your invention can start a
        clock on your patent rights, or forfeit them outright in many countries.
        That is a conversation to have with a practitioner before you disclose
        anything publicly.
      </p>

      <h2>What we provide, and its limits</h2>
      <p>
        We provide AI-generated strategic analysis, prior-art search, and
        educational content, drawn from automated processing of public patent
        and market databases.
      </p>
      <p>
        <strong>It can be wrong.</strong> AI systems produce confident-sounding
        output that is sometimes inaccurate, incomplete, or out of date. Verify
        anything you intend to act on.
      </p>
      <p>
        <strong>A search here is not an exhaustive search.</strong> Our prior-art
        search covers public databases available to us at the time it runs. It
        does not cover unpublished pending applications, and it is not a
        substitute for a professional search or an attorney&apos;s freedom-to-operate
        opinion.
      </p>
      <p>
        Verdicts, scores, and recommendations are opinions generated from the
        information you provided. They are not predictions, guarantees, or
        assurances of any commercial or legal outcome.
      </p>

      <h2>The practitioner marketplace</h2>
      <p>
        We list patent attorneys and agents from the public USPTO register so
        you can find one. Listing is not endorsement, recommendation, or
        vetting. We do not verify their current standing, competence,
        availability, or fees.
      </p>
      <p>
        Any engagement you enter into with a practitioner is between you and
        them. We are not a party to it and are not responsible for their work,
        their advice, or their conduct.
      </p>

      <h2>Subscriptions, billing, and refunds</h2>
      <ul>
        <li>Free features remain free, and no card is required to use them.</li>
        <li>
          <strong>Exports come with paid memos.</strong> The confidential PDF,
          share links, Send to Google Docs or Notion, and the calendar feed work
          for every memo while you subscribe. If your subscription ends, those
          exports pause for memos made on it and return if you subscribe again.
          Memos you paid for with a session, pack, or credit keep their exports
          for good. Copies already in your own Google Drive or Notion stay
          yours.
        </li>
        <li>
          <strong>All prices are in United States dollars.</strong> If your card
          is issued elsewhere, your bank converts at its own rate.
        </li>
        <li>
          Paid subscriptions renew automatically at the interval you chose,
          until you cancel. You can cancel any time from your account settings,
          and cancelling stops the next renewal rather than ending your current
          period.
        </li>
        <li>
          One-time purchases, such as a search report, are charged once and are
          not subscriptions.
        </li>
        <li>
          We may change prices. A change never affects the period you have
          already paid for, and we will tell you before it applies to a renewal.
        </li>
        <li>
          Prices exclude any tax we are required to collect, which is shown at
          checkout where it applies.
        </li>
        <li>
          If a payment fails we may retry it and may suspend paid features until
          it clears.
        </li>
        <li>
          Refunds are handled case by case. Contact us within 14 days and we
          will deal with you fairly.
        </li>
        <li>
          Payments are processed by Stripe. We do not receive or store your full
          card details.
        </li>
      </ul>

      <h2>Acceptable use</h2>
      <p>Do not use <Wordmark size="inherit" /> to:</p>
      <ul>
        <li>Break the law, harass anyone, or defraud anyone.</li>
        <li>
          Submit content you have no right to submit, including someone
          else&apos;s confidential information or intellectual property.
        </li>
        <li>
          Reverse-engineer the service, scrape it at volume, or use it to build
          a competing product.
        </li>
        <li>
          Circumvent usage limits, share one account across people, or resell
          access.
        </li>
        <li>
          Attempt to extract another user&apos;s content, or interfere with the
          service&apos;s operation or security.
        </li>
      </ul>
      <p>We may suspend or close accounts that abuse the platform.</p>

      <h2>Feedback</h2>
      <p>
        If you send us suggestions about the product, we may use them freely and
        without obligation to you. This covers feedback about{' '}
        <Wordmark size="inherit" /> itself. It does not touch your ideas, your
        content, or anything in your account, which remain entirely yours.
      </p>

      <h2>Our intellectual property</h2>
      <p>
        The service itself, including its software, design, brand, lessons, and
        written materials, belongs to us. Your subscription is permission to use
        it, not a transfer of ownership. Your own content and deliverables are
        excluded from this and remain yours.
      </p>

      <h2>Copyright complaints</h2>
      <p>
        If you believe material on <Wordmark size="inherit" /> infringes your
        copyright, write to{' '}
        <a href="mailto:legal@entredash.com" style={{ color: 'var(--brass)' }}>
          legal@entredash.com
        </a>{' '}
        identifying the work, where the material is, your contact details, and a
        statement that you believe in good faith the use is unauthorized. We
        respond to valid notices under the Digital Millennium Copyright Act and
        will remove infringing material.
      </p>

      <h2>Ending the agreement</h2>
      <p>
        You can stop using the service and close your account at any time from
        your account settings. Closing your account deletes your content.
      </p>
      <p>
        We may suspend or close your account if you materially breach these
        terms, if we are required to by law, or if we discontinue the service.
        Except where you have breached these terms, we will give you reasonable
        notice and refund the unused portion of anything you have prepaid.
      </p>
      <p>
        Before you go, you can export your data from your account settings. The
        sections on ownership, confidentiality, disclaimers, liability,
        indemnification, and disputes survive the end of this agreement.
      </p>

      <h2>Service availability</h2>
      <p>
        We aim to keep <Wordmark size="inherit" /> running and available, but we
        do not promise uninterrupted service. We may take it down for
        maintenance, and we may change or discontinue features. If we
        discontinue a paid feature you are currently paying for, we will refund
        the unused portion.
      </p>

      <h2>Disclaimers</h2>
      <p>
        <Wordmark size="inherit" /> is provided &quot;as is&quot; and &quot;as
        available.&quot; To the fullest extent the law allows, we disclaim all
        warranties, whether express or implied, including any implied warranties
        of merchantability, fitness for a particular purpose, non-infringement,
        and accuracy.
      </p>
      <p>
        We do not warrant that the service will be uninterrupted, error-free, or
        secure, that its output will be accurate or complete, or that using it
        will produce any particular result, including a granted patent or a
        commercial outcome.
      </p>

      <h2>Limitation of liability</h2>
      <p>
        To the fullest extent the law allows, neither party is liable for
        indirect, incidental, special, consequential, or punitive damages, or
        for lost profits, lost revenue, lost data, or lost business
        opportunities, even if warned they were possible.
      </p>
      <p>
        Our total liability to you for any claim arising out of or relating to
        the service is limited to the greater of the amount you paid us in the
        12 months before the claim arose, or 100 US dollars.
      </p>
      <p>
        <strong>
          We are not liable for decisions you make about your intellectual
          property based on our analysis.
        </strong>{' '}
        That includes filing or not filing, disclosing or not disclosing, and
        spending or not spending. Consult a licensed patent attorney before
        relying on any memo we produce.
      </p>
      <p>
        Some places do not allow these exclusions. Where that is true, they do
        not apply to you, and nothing here limits liability for fraud, or for
        death or personal injury caused by negligence.
      </p>

      <h2>Indemnification</h2>
      <p>
        You agree to cover us against claims, losses, and reasonable legal costs
        arising from your breach of these terms, your misuse of the service, or
        content you submitted that you had no right to submit. We will tell you
        promptly about any such claim and let you control the defense of it,
        provided any settlement releases us fully.
      </p>

      <h2>Disputes</h2>
      <p>
        <strong>Talk to us first.</strong> Most problems are solved by an email
        to{' '}
        <a href="mailto:legal@entredash.com" style={{ color: 'var(--brass)' }}>
          legal@entredash.com
        </a>
        . Please give us 30 days to work it out before starting a formal
        proceeding.
      </p>
      <p>
        If that does not resolve it, you and we agree to settle any dispute by
        binding individual arbitration administered by the American Arbitration
        Association under its Consumer Arbitration Rules, rather than in court
        before a judge or jury. Arbitration is conducted remotely by video or
        telephone wherever possible. If an in-person hearing is needed, it takes
        place in the county where you live, or somewhere else we both agree on.
      </p>
      <p>
        <strong>
          Disputes are resolved individually. There are no class actions, and no
          consolidated or representative proceedings.
        </strong>
      </p>
      <p>
        <strong>You can opt out of arbitration.</strong> Email{' '}
        <a href="mailto:legal@entredash.com" style={{ color: 'var(--brass)' }}>
          legal@entredash.com
        </a>{' '}
        within 30 days of first accepting these terms, saying you opt out of
        arbitration. Opting out costs you nothing and changes nothing else about
        your account.
      </p>
      <p>
        Either of us may still bring a claim in small claims court, and either
        of us may seek an injunction in court to protect intellectual property
        or confidential information.
      </p>
      <p>
        These terms are governed by the laws of the State of Delaware, where
        EntreDash, Inc. is incorporated, without regard to its conflict-of-laws
        rules. Where arbitration does not apply, disputes go to the state or
        federal courts in Dallas County, Texas, where we operate.
      </p>

      <h2>Changes to these terms</h2>
      <p>
        We may update these terms. The date at the top always reflects the last
        substantive change. For material changes we will tell you by email or in
        the product before they take effect, and continued use afterwards means
        you accept them. If you would rather not, you can close your account and
        we will refund the unused portion of anything prepaid.
      </p>

      <h2>The rest</h2>
      <ul>
        <li>
          If any part of these terms is unenforceable, the rest stays in force.
        </li>
        <li>
          These terms, with our Privacy Policy and Cookie Policy, are the whole
          agreement between us about the service.
        </li>
        <li>
          You may not transfer this agreement. We may transfer it as part of a
          merger, acquisition, or sale of assets.
        </li>
        <li>
          If we do not enforce something immediately, we have not given up the
          right to enforce it later.
        </li>
        <li>
          Neither of us is responsible for failures caused by events genuinely
          outside our control.
        </li>
        <li>
          Nothing in these terms creates a partnership, joint venture, or
          employment relationship.
        </li>
      </ul>

      <h2>Contact</h2>
      <p>
        EntreDash, Inc. is a Delaware corporation. Notices, questions, and
        anything else about these terms:
      </p>
      <address>
        EntreDash, Inc.
        <br />
        3723 Greenville Avenue STE 45690
        <br />
        Dallas, TX 75206
        <br />
        United States
        <br />
        <a href="mailto:legal@entredash.com" style={{ color: 'var(--brass)' }}>
          legal@entredash.com
        </a>
      </address>
      {/* The registered agent address is listed separately and ONLY for service
          of process. It is not a mailing address: a registered agent accepts
          legal process and official state correspondence, and a customer letter
          or a DMCA notice sent there would not reliably reach the company. That
          distinction is why the Dallas address stays above rather than being
          replaced, and why it remains the CAN-SPAM address in email footers —
          that must be somewhere mail actually goes. */}
      <p>
        Formal legal process may be served on our registered agent in Delaware:
      </p>
      <address>
        131 Continental Drive, Suite 305
        <br />
        Newark, DE 19713
        <br />
        United States
      </address>
    </>
  );
}

export function TermsPage() {
  return (
    <LegalShell title="Terms of Service">
      <TermsBody />
    </LegalShell>
  );
}

export function PrivacyBody() {
  const link = { color: 'var(--brass)' } as const;
  return (
    <>
      <p>
        <em>Last updated: {LEGAL_UPDATED.privacy}</em>
      </p>
      <p>
        <em>
          This version, with an updated list of vendors who help us run
          EntreDash, takes effect for existing members on November 3, 2026.
          Until then, the previous version applies to you.
        </em>
      </p>
      <p>
        Your ideas are confidential. Your data is yours. This policy explains
        what <Wordmark size="inherit" /> collects, why, who helps us run the
        service, how long we keep things, and what you can do about all of it.
      </p>
      <p>
        EntreDash, Inc. (&quot;EntreDash&quot;, &quot;we&quot;) is the company
        responsible for your data. We are a Delaware corporation based in
        Dallas, Texas. Reach us at{' '}
        <a href="mailto:privacy@entredash.com" style={link}>privacy@entredash.com</a>{' '}
        or by mail at 3723 Greenville Avenue STE 45690, Dallas, TX 75206,
        United States.
      </p>

      <h2>What we collect</h2>
      <ul>
        <li>
          <strong>Account and profile:</strong> your name, and the email address
          or mobile number you sign in with. If you sign in by phone, a contact
          email so we can reach you. Profile details you choose to add: country,
          state or province, city, profession, education level, technical
          background, areas of interest and preferred currency.
        </li>
        <li>
          <strong>Your invention work:</strong> everything you create, including
          idea descriptions and versions, advisor sessions, memos, design
          breakdowns and drawing briefs, customer discovery, business canvases,
          prior-art search strategies and reports, planning, monitoring, tags,
          collections, your notes and your highlights on memos and lessons, your
          invention log entries with their earlier versions, saved versions of your
          design breakdown, conception narratives written for you, and anything you
          upload, including log photos and files, your Prepare My Filing drafts and
          drawings, every Word and PDF file you download from Prepare My Filing (we keep
          each one exactly as it was made, so the copy you record as filed never
          changes), and the filings, deadlines and receipts you track in the Cost &amp;
          Filing Manager. For each printed or downloaded log we
          keep a check record (its date, entry count and fingerprints, never its
          contents) so the copy can be checked later. It is confidential and visible only to you.
          Our AI advisors do not read your notes or your invention log, with two
          exceptions, both started by you. When you ask for a conception
          narrative, the AI reads that idea&apos;s log entries and design breakdown
          to write it. And if you switch on &ldquo;Use my invention log&rdquo; for a
          Prepare My Filing draft, your latest log entries are sent when writing
          that draft&apos;s detailed description, and for no other section.
        </li>
        <li>
          <strong>Pathway Navigator:</strong> if you use it for an idea, we keep your
          session answers (such as where you live and invented it, who owns it, any
          public disclosure and its date, your markets, budget and goals), every
          strategy version written from them, and your questions to the filing
          strategist with its answers. The AI reads these, with that idea&apos;s IP
          memo and prior-art findings, to write your strategy and answer your
          questions. Our monthly check of patent office rules reads only the offices&apos;
          public pages, never your answers.
        </li>
        <li>
          <strong>People who witness your log:</strong> if you ask someone to witness
          log entries, the person who signs gives us their name, their email and,
          if they choose, how they know you. We keep these with the signature, along
          with the date and which entry versions they signed. If you have us email
          the invite, we keep the address you typed only until the request is
          signed, cancelled or expires. A witness sees your name, the idea&apos;s
          title and the text of the entries you chose, never your photos, files or
          the rest of your log.
        </li>
        <li>
          <strong>Connected accounts:</strong> if you connect Google Drive or Notion
          to export memos, we store the access credentials they give us, with an
          extra layer of encryption, plus your Notion workspace name and a record of
          each memo you send there (where it went and its link). You can disconnect
          at any time.
        </li>
        <li>
          <strong>Billing:</strong> if you buy a plan or a pack, our payment
          processor, Stripe, collects your card details. We never see or store
          full card numbers. We keep a record of what you bought, when, and for
          how much.
        </li>
        <li>
          <strong>Usage:</strong> which features you use and when, so we can run
          allowances and improve the product. We never use your invention content
          for this.
        </li>
        <li>
          <strong>Site visits:</strong> pages viewed, how you arrived, your
          country and your device type. We do not store your IP address for our
          own analytics.
        </li>
        <li>
          <strong>Where you came from:</strong> when you first arrive, we note the
          name of the website that linked to us (for example a search engine or a
          directory, never the full web address), any campaign tags in our own
          links, and the referral code if a member shared their link with you. We
          use this only to see which places send us visitors, and never share it
          with advertising platforms. It is recorded only if you allow analytics
          in our cookie settings; if you don&apos;t, or you later turn analytics
          off, it isn&apos;t kept. It is kept in your browser&apos;s local storage
          and in our own records, and clearing your browser&apos;s site data
          removes it from your browser.
        </li>
        <li>
          <strong>Advertising measurement, only if you accept:</strong> nothing
          from an advertising platform loads until you allow marketing in our
          cookie settings. If you do, Google, Meta, LinkedIn and Reddit receive a narrow
          set of signals when you visit, sign up or buy: pages viewed, a one-way
          hash of your email or phone (never the address itself), your IP address,
          and for purchases the plan and price. They never receive idea or memo
          content. Change your choice any time from the Cookie preferences link in
          the footer.
        </li>
        <li>
          <strong>How our public pages are used, only if you allow analytics:</strong>{' '}
          Microsoft Clarity records clicks, scrolling and page layout on our public
          marketing pages so we can fix what&apos;s confusing. It never runs inside
          your signed-in account, and it masks what you type. It has its own
          switch in cookie settings.
        </li>
        <li>
          <strong>Messages you send us:</strong> feedback, support emails, survey
          answers and feature requests.
        </li>
      </ul>

      <h2>How we use it, and why we&apos;re allowed to</h2>
      <p>For members in the EU and UK, each use has a legal basis:</p>
      <ul>
        <li>
          <strong>Performing our contract with you:</strong> running your account
          and workspaces, generating the AI analysis and memos you ask for,
          researching your idea against public records, and sending the account
          and memo emails you need.
        </li>
        <li>
          <strong>Contract and our legal duty to keep tax records:</strong>{' '}
          taking payment and keeping billing records.
        </li>
        <li>
          <strong>Our legitimate interest:</strong> keeping the service secure,
          preventing fraud and abuse, and improving features using aggregate
          usage patterns.
        </li>
        <li>
          <strong>Your consent, which you can withdraw at any time:</strong> the
          newsletter and advertising measurement.
        </li>
      </ul>
      <p>
        When we research your idea against patent, academic and web-search
        services, we send search terms drawn from it, not the idea itself.
      </p>

      <h2>What we will never do</h2>
      <ul>
        <li>Sell your data to anyone.</li>
        <li>
          Share your invention content with anyone without your explicit consent. A memo
          share link is that consent: you make it, it shows one memo version and your name
          to whoever has the link, and you can revoke it at any time.
        </li>
        <li>
          Use your invention content to train AI models. Our AI providers are
          contractually prohibited from training on what we send them.
        </li>
        <li>
          Give idea or memo content to any advertising or analytics tool. This is
          enforced in code, not just in this policy.
        </li>
        <li>
          Read or look at your invention content, unless you choose to share it
          with us (for example, by pasting part of it into a support message).
          We handle support and investigate security issues using account,
          billing and activity records, never your ideas. The one exception is a
          valid court or government order that legally requires us to hand data
          over. If that ever happens, we will tell you first unless the law
          forbids it.
        </li>
      </ul>

      <h2>Who helps us run EntreDash</h2>
      <p>
        Each of these vendors processes data only on our instructions and under
        a data processing agreement.
      </p>
      <p>
        <em>
          This list was updated on 20 October 2026, and the changes take effect
          on 3 November 2026. We emailed members about them on 20 October 2026.
          Later the same day we also limited and clarified what Prepare My
          Filing sends to Anthropic, clarified that Google writes full idea titles (both providers
          already listed), added a switch to turn off deadline reminders, and
          made visitor source tracking wait for analytics consent. We are
          emailing members about these clarifications separately.
        </em>
      </p>

      <h3>Core service</h3>
      <ul>
        <li>
          <strong>MindStudio (Remy)</strong>, using Amazon Web Services, Google
          Cloud and Cloudflare: hosting, database, file storage, PDF rendering,
          and email and text-message delivery. Receives everything the app
          stores, encrypted in transit and at rest. Kept while your account
          exists and deleted when you delete it, as described below.
        </li>
        <li>
          <strong>Stripe:</strong> payments. Receives your name, email, plan, and
          the card details you enter with Stripe. Your invention titles are not
          sent. Stripe keeps payment records under its own financial-record
          rules.
        </li>
      </ul>

      <h3>AI models</h3>
      <p>
        Each receives only the content needed for each request, and none of them
        may use it to train their models. We use each provider&apos;s commercial
        API, and its commercial terms don&apos;t allow training on customer
        content.
      </p>
      <ul>
        <li>
          <strong>Anthropic (Claude):</strong> advisor sessions, memos, the
          business canvas, Spark ideation (including its built-in web search) and
          3D render checks. Receives your idea description, the conversation and
          your answers. For Prepare My Filing drafting, it receives the technical
          description of your invention, over an encrypted connection: your
          idea&apos;s problem and solution, the IP Consultant&apos;s inventive
          steps, your drawing brief and the prior art from your search report.
          Your invention log is sent only if you switch it on for a draft, and
          then only when writing the detailed description. Your name, contact
          details, market research and filing plan are never sent.
        </li>
        <li>
          <strong>Google (Gemini):</strong> advisor sessions, checks that a
          conversation stays on one invention, sketch generation and text checks,
          filing-drawing review and redraws, concept-model renders, and writing a
          full title for an idea when the one you gave was cut off. Receives
          the same, plus your drawings and design images when you use those
          tools, sent as private links that expire after 15 to 30 minutes.
        </li>
        <li>
          <strong>OpenAI:</strong> some advisor turns and 3D concept-model design.
          Receives the content needed for each request, including your design
          description and part list.
        </li>
        <li>
          <strong>WaveSpeed (Hyper3D Rodin):</strong> building the 3D mesh for a
          concept model. Receives rendered images of your design, with no name or
          account details.
        </li>
      </ul>

      <h3>Research and search</h3>
      <ul>
        <li>
          <strong>Google Search and public web pages</strong>, through our
          platform&apos;s search service: research for memos, monitoring and
          filing strategy, such as finding competitors, market figures and filing
          rules. Receives search queries derived from your idea (short phrases
          about the product and its field). Our research then reads public web
          pages.
        </li>
        <li>
          <strong>Google News</strong>, through our platform&apos;s search service:
          news for your ideas in Signals. Receives a few short, general keywords
          about your idea&apos;s field. Your description, the new part of your
          invention, your name and your account are never sent.
        </li>
        <li>
          <strong>Patent and research databases</strong> (USPTO, European Patent
          Office, Google Patents, OpenAlex, PubMed): prior-art and literature
          search. Receive search terms only, never account data.
        </li>
        <li>
          <strong>PQAI</strong> (an open-source prior-art search, currently
          switched off): if we switch it on, it receives a short technical
          description written by our search strategy: the technical field, the
          features restated in patent language, and keywords. Never your own
          wording, your name or your account.
        </li>
        <li>
          <strong>openFDA and Open Food Facts:</strong> food and ingredient checks
          in food memos. Receive ingredient and product-type terms only.
        </li>
      </ul>

      <h3>Only if you choose to</h3>
      <ul>
        <li>
          <strong>Google Drive</strong>, only if you connect it: receives the
          memos you export, and holds your Google credentials.
        </li>
        <li>
          <strong>Notion</strong>, only if you connect it: receives the memos you
          send, in the pages and databases you choose on Notion&apos;s own screen.
          EntreDash can&apos;t see anything else in your workspace.
        </li>
        <li>
          <strong>Beehiiv:</strong> newsletter delivery. Receives your email and
          name, only if you subscribe.
        </li>
        <li>
          <strong>Google, Meta, LinkedIn and Reddit:</strong> advertising
          measurement, only if you accept advertising cookies. For sign-ups and
          purchases they receive a hashed email, phone number and account id,
          plus your IP address and browser type. Never your ideas.
        </li>
        <li>
          <strong>Microsoft Clarity:</strong> public-page analytics, only if you
          allow analytics. Sees public pages only, never account content.
        </li>
      </ul>

      <h3>People you invite</h3>
      <ul>
        <li>
          <strong>A witness you invite</strong> from your invention log: receives
          your name, the idea&apos;s title and the entries you chose. The
          witness&apos;s own name and email are stored with their signature, and
          their email is confirmed by a link we send them.
        </li>
      </ul>
      <p>
        The platform&apos;s own subprocessors are listed on the{' '}
        <a href="https://trust.mindstudio.ai/" target="_blank" rel="noopener noreferrer" style={link}>
          MindStudio Trust Center
        </a>
        . We update this list before any new vendor receives your personal data.
      </p>

      <h2>Where your data is kept</h2>
      <p>
        Our servers are in the United States, so your data is stored and
        processed there. If you are in the EU, the UK or elsewhere, your data is
        transferred to the United States. Where the law requires it, we rely on
        standard contractual clauses or equivalent safeguards provided by our
        vendors.
      </p>

      <h2>How long we keep it</h2>
      <ul>
        <li>
          <strong>Your account and all your invention work, including your
          notes, highlights and invention log:</strong> until you delete it, or
          delete your account. Removing a log entry hides it but keeps it in your
          record; deleting the idea or your account erases the log, its files, its
          narratives and design versions, its witness requests and signatures, and its
          check records, so a printed check code then says the record can't be found.
        </li>
        <li>
          <strong>Pathway Navigator answers, strategies and strategist chat:</strong>{' '}
          until you delete the idea or your account, which erases them.
        </li>
        <li>
          <strong>Prepare My Filing drafts, downloads and filed copies, and your Cost &amp;
          Filing Manager record:</strong> until you delete the idea or your account,
          which erases the draft, its versions and drawings, every downloaded file
          (including any you recorded as filed) and the tracked filings, deadlines and
          receipts. They stay while an invention is read-only after its access ends.
          Payment records are kept as described below. A filed copy is your own
          record of what you downloaded, not proof of filing.
        </li>
        <li>
          <strong>Deadline reminder emails:</strong> the Cost &amp; Filing Manager
          emails you 90, 30 and 7 days before each filing deadline, showing the
          deadline&apos;s name and date only. They continue after a plan or pack
          ends, so a deadline is never missed silently, until you mark it done or
          switch reminders off in Settings, Preferences. A record that each
          reminder was sent is kept for about 13 months.
        </li>
        <li>
          <strong>Witness signatures:</strong> kept with the log they belong to, and
          erased with it. A witness link stops working when it is signed, cancelled
          or expires; the invite email address is deleted at that point.
        </li>
        <li>
          <strong>Memo share links you create, and how often each was opened:</strong>{' '}
          until you revoke them or delete your account. A link stops working when it
          expires or is revoked.
        </li>
        <li>
          <strong>Something you delete:</strong> removed from the app right away,
          and from backups when the platform&apos;s backup copies expire.
        </li>
        <li>
          <strong>Billing and purchase records:</strong> 7 years, for tax.
        </li>
        <li>
          <strong>Newsletter subscription:</strong> until you unsubscribe. After
          that we keep only your email address and the date, so we never email
          you again.
        </li>
        <li>
          <strong>Security and admin audit records:</strong> kept, with personal
          details such as email addresses removed after 3 years.
        </li>
      </ul>

      <h2>Your choices and rights</h2>
      <p>Everyone can, at any time:</p>
      <ul>
        <li>
          <strong>Download everything you&apos;ve created</strong> as one file,
          from{' '}
          <Link href="/settings/privacy" style={link}>Settings → Privacy</Link>.
        </li>
        <li><strong>Correct</strong> your profile, from Settings.</li>
        <li>
          <strong>Delete</strong> any idea, or your whole account, from Settings.
          Deleting your account removes your ideas and everything attached to
          them, cancels any active subscription and disconnects Google and Notion. We keep
          only billing records for tax, and an unsubscribe record so we never
          email you again.
        </li>
        <li><strong>Stop marketing</strong> from the link in any email, or in Settings.</li>
        <li>
          <strong>Turn advertising measurement on or off</strong> from the Cookie
          preferences link in the footer.
        </li>
      </ul>
      <p>
        <strong>If you are in the EU, the UK or the European Economic Area,</strong>{' '}
        you also have the right to ask for a copy of your personal data, to have
        it corrected or deleted, to restrict how we use it, to object to our
        using it on the basis of legitimate interest, to receive it in a portable
        format, and to withdraw consent at any time without affecting what we did
        before. You can also complain to your local data protection authority; in
        the UK that is the Information Commissioner&apos;s Office (ico.org.uk).
      </p>
      <p>
        <strong>If you live in a US state with a privacy law</strong> (such as
        California, Colorado, Connecticut, Virginia or Texas), you may have
        similar rights to know, access, correct and delete your data, and to opt
        out of targeted advertising. We don&apos;t sell personal data.
        Advertising measurement only runs if you accept it, and declining or
        withdrawing in Cookie preferences opts you out.
      </p>
      <p>
        To use any right the app doesn&apos;t handle directly, email{' '}
        <a href="mailto:privacy@entredash.com" style={link}>privacy@entredash.com</a>{' '}
        from the address on your account. We&apos;ll confirm it&apos;s you and
        respond within 30 days. We won&apos;t treat you differently for using
        your rights.
      </p>

      <h2>Security</h2>
      <p>
        Your data is encrypted in transit and at rest, and your work is visible
        only to your account, checked on our servers on every request. Sign-in
        uses one-time codes, so there are no passwords to leak. If a breach
        affects your data, we&apos;ll tell you directly and follow the
        notification timelines the law requires. Read more on our{' '}
        <Link href="/security" style={link}>Trust &amp; Security</Link> page, or
        report a concern to{' '}
        <a href="mailto:security@entredash.com" style={link}>security@entredash.com</a>.
      </p>

      <h2>Children</h2>
      <p>
        You must be at least 16 to use <Wordmark size="inherit" />. Members aged
        16 or 17 may use <Wordmark size="inherit" /> with a parent&apos;s or
        guardian&apos;s consent, and may buy a subscription or make a purchase
        only with that parent&apos;s or guardian&apos;s permission, using a payment
        method they have authorized. We do not knowingly collect data
        from anyone under 16. If you believe someone under 16 has given us data,
        email privacy@entredash.com and we&apos;ll delete it.
      </p>

      <h2>Changes to this policy</h2>
      <p>
        We&apos;ll email you at least 14 days before a material change takes
        effect, and update the date at the top. Continuing to use EntreDash after
        that date means you accept the updated policy.
      </p>

      <h2>Contact</h2>
      <p>
        Questions or requests:{' '}
        <a href="mailto:privacy@entredash.com" style={link}>privacy@entredash.com</a>.
        Security concerns:{' '}
        <a href="mailto:security@entredash.com" style={link}>security@entredash.com</a>.
      </p>
    </>
  );
}

export function PrivacyPage() {
  return (
    <LegalShell title="Privacy Policy">
      <PrivacyBody />
    </LegalShell>
  );
}

/**
 * Cookie Policy. A short, plain-language explanation of which cookies the
 * app sets and why. Linked from the footer. Authored as a real document so
 * it doesn't read as filler.
 */
export function CookiesPage() {
  const link = { color: 'var(--brass)' } as const;
  return (
    <LegalShell title="Cookie Policy">
      <p>
        <em>Last updated: {LEGAL_UPDATED.cookies}</em>
      </p>
      <p>
        <Wordmark size="inherit" /> uses the cookies and browser storage it needs
        to work. With your permission, it also uses Microsoft Clarity to see how
        our public pages are used, and advertising tools from Google, Meta,
        LinkedIn and Reddit to measure our ads. Nothing from those companies
        loads until you allow it in Cookie settings, wherever you are, and none of them ever sees
        your idea or memo content.
      </p>

      <h2>Essential (always on)</h2>
      <p>These are required for the service to work. They carry no advertising.</p>
      <ul>
        <li>
          <strong>Session cookie.</strong> Keeps you signed in after you verify
          your email or phone. Cleared when you sign out.
        </li>
        <li>
          <strong>Anonymous-session cookie.</strong> Lets you start a strategy
          session before creating an account. Merged with your account when you
          sign up. Cleared after 30 days of inactivity.
        </li>
        <li>
          <strong>Preferences storage.</strong> Small choices kept in your
          browser&apos;s local storage, such as sidebar state, tips you dismissed
          and your cookie choice. Never sent to our servers.
        </li>
      </ul>

      <h2>Advertising (only if you accept)</h2>
      <ul>
        <li>
          <strong>Google tag (Google Ads).</strong> Tells us which Google ads bring
          people to EntreDash: which pages you visit and whether you sign up or buy.
        </li>
        <li>
          <strong>Meta Pixel.</strong> The same, for our Facebook and Instagram ads.
        </li>
        <li>
          <strong>Reddit Pixel.</strong> The same, for our Reddit ads.
        </li>
      </ul>
      <p>
        When you sign up, start a checkout or subscribe, our servers can also send
        Meta, LinkedIn and Reddit a short confirmation of that event, so the
        measurement doesn&apos;t depend on a browser tag surviving an ad blocker.
        It carries a one-way hash of your email (and, for Meta, your phone if we
        have it), your IP address and browser type, and for a purchase the plan
        and price. These server calls follow the same choice: they only happen if
        you accepted.
      </p>
      <p>
        We don&apos;t load LinkedIn&apos;s browser tag; LinkedIn ads are measured
        only through the server calls above. All of these are covered by one
        choice. Accepting
        or declining applies to all of them. If your browser sends a Global
        Privacy Control signal, we treat it as a decline automatically.
      </p>

      <h2>Analytics (only if you allow it)</h2>
      <ul>
        <li>
          <strong>Microsoft Clarity.</strong> Shows us how visitors use our public
          pages, such as clicks, scrolling and where people get stuck, so we can
          improve them. It runs only on public pages, never inside your signed-in
          account, and masks what you type. It has its own switch in Cookie
          settings, separate from advertising.
        </li>
      </ul>

      <h2>Site statistics</h2>
      <p>
        Our hosting platform counts visits, pages viewed, where visitors came
        from, their country and device type for everyone. It sets no cookies,
        keeps no IP addresses and uses no fingerprinting.
      </p>
      <p>
        We also keep our own record of where each visitor first came from: the
        name of the referring website (not its full address), campaign tags in our
        own links, and a member&apos;s referral code if one was used. It is stored
        in your browser&apos;s local storage, not in a cookie, is never shared with
        advertising platforms, and is used only to see which places send us
        visitors. Clearing your browser&apos;s site data removes it.
      </p>

      <h2>Fonts, icons and images</h2>
      <p>
        Our fonts and interface icons are served from our own servers. Some pages
        show badges from directories that list EntreDash; those images load from
        the directory&apos;s own website, as any image link does.
      </p>

      <h2>Your control</h2>
      <p>
        Change your choice any time from the <strong>Cookie preferences</strong>{' '}
        link in the footer. Declining takes effect immediately: the advertising
        tools stop, and the server calls stop too. You can also clear cookies from
        your browser settings; clearing the session cookie signs you out. See our{' '}
        <Link href="/privacy" style={link}>Privacy Policy</Link> for more.
      </p>

      <h2>Questions</h2>
      <p>
        Email{' '}
        <a href="mailto:privacy@entredash.com" style={link}>
          privacy@entredash.com
        </a>{' '}
        with anything cookie-related.
      </p>
    </LegalShell>
  );
}

/**
 * Blog landing page. A pre-launch placeholder — the editorial roadmap for
 * the Education Center team to publish guides, deep dives, and inventor
 * stories. Built as a real page (not a 404 redirect) so the footer link
 * always lands somewhere coherent.
 */
export function BlogPage() {
  const teasers = [
    {
      tag: 'Guide',
      title: 'How to read a patent without a law degree.',
      summary:
        'A walkthrough of the parts of a patent — claims, specification, drawings — and what each one is actually telling you.',
    },
    {
      tag: 'Inventor story',
      title: 'From shower thought to provisional in six weeks.',
      summary:
        'The play-by-play of how one solo inventor went from an idea on a Sunday morning to a filed provisional, with the real costs and timeline.',
    },
    {
      tag: 'Strategy',
      title: 'When trade secret beats patent (and when it really doesn\u2019t).',
      summary:
        'A practical decision tree for inventors weighing whether to file or keep their work confidential, with examples of when each path wins.',
    },
    {
      tag: 'Deep dive',
      title: 'Anatomy of a great independent claim.',
      summary:
        'We dissect five real claims from granted patents, showing what makes one defensible and another easy to design around.',
    },
  ];

  return (
    <LegalShell title="The Inventor's Notebook">
      <p>
        Long-form writing for the everyday person with an idea. Guides,
        inventor stories, and plain-language deep dives on how IP actually
        works. The first posts go live this summer — drop your email in the
        footer feedback form and we&apos;ll let you know when the inaugural
        issue is out.
      </p>

      <h2>What&apos;s coming</h2>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: 24,
          marginTop: 24,
          marginBottom: 32,
        }}
      >
        {teasers.map((t) => (
          <div
            key={t.title}
            style={{
              padding: '24px 28px',
              border: '1px solid var(--border-hairline)',
              borderRadius: 'var(--r-md)',
              background: 'var(--surface-card)',
            }}
          >
            <div
              className="f-mono"
              style={{
                fontSize: 11,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'var(--brass)',
                marginBottom: 10,
              }}
            >
              {t.tag} · coming soon
            </div>
            <div
              style={{
                fontFamily: 'var(--f-serif)',
                fontSize: 22,
                fontWeight: 500,
                color: 'var(--ink)',
                marginBottom: 8,
                lineHeight: 1.3,
              }}
            >
              {t.title}
            </div>
            <p
              style={{
                fontSize: 14,
                lineHeight: 1.6,
                color: 'var(--text-decorative)',
                margin: 0,
              }}
            >
              {t.summary}
            </p>
          </div>
        ))}
      </div>

      <h2>The editorial principle</h2>
      <p>
        Every post is written for someone who has never met a patent attorney.
        No legalese. No empty optimism. Real numbers, real timelines, and the
        kind of context that makes the next conversation with a real attorney
        productive instead of intimidating.
      </p>
    </LegalShell>
  );
}

/**
 * Trust & Security page. The single most important non-product page in
 * the app — addresses the #1 blocker we observed in tester research
 * ("could my idea get stolen?") and provides the documentation that
 * outside counsel will reference when evaluating whether using
 * EntreDash constitutes a "public disclosure" under §102(b).
 *
 * Every concrete claim here is sourced from MindStudio's public
 * Trust Center (https://trust.mindstudio.ai). Do not add claims that
 * aren't independently verifiable there.
 */
export function SecurityBody() {
  return (
    <>
      <p>
        <em>Last updated: {LEGAL_UPDATED.confidentiality}</em>
      </p>
      <p>
        Your invention is the most valuable thing you bring to{' '}
        <Wordmark size="inherit" />. This page exists so you know exactly
        how we handle it. Every claim below is independently verifiable
        on MindStudio&apos;s{' '}
        <a
          href="https://trust.mindstudio.ai/"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'var(--brass-deep)' }}
        >
          Trust Center
        </a>
        .
      </p>

      <h2>The three things you need to know</h2>
      <ul>
        <li>
          <strong>Your inputs are never used to train AI models.</strong>{' '}
          Not by us, not by any AI provider we route to. This is{' '}
          <a
            href="https://trust.mindstudio.ai/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--brass-deep)' }}
          >
            stated directly
          </a>{' '}
          by our infrastructure provider and contractually enforced with
          every model in the catalog.
        </li>
        <li>
          <strong>Your data sits on SOC 2 Type II-audited infrastructure.</strong>{' '}
          We run on MindStudio, whose platform passes that audit annually
          through an independent third party. It&apos;s the same audit
          standard relied on by enterprise SaaS like Notion and Linear.
        </li>
        <li>
          <strong>You can delete everything at any time.</strong> Each
          idea, each memo, each strategy session, and your entire
          account — gone with one click in Settings.
        </li>
      </ul>

      <h2>Two layers of security</h2>
      <p>
        It helps to be precise about what is certified by whom.{' '}
        <Wordmark size="inherit" /> runs entirely on{' '}
        <strong>MindStudio</strong>, an independently audited
        infrastructure platform. That means your data inherits a strong
        set of platform-level controls, and we add our own application
        and data-handling practices on top.
      </p>
      <ul>
        <li>
          <strong>The infrastructure layer (MindStudio).</strong>{' '}
          Holds a <strong>SOC 2 Type II</strong> attestation, the
          rigorous continuous-controls audit covering encryption, access
          management, change management, and incident response operating
          effectively over a multi-month period, verified annually by an
          independent third party. MindStudio is also <strong>GDPR</strong>{' '}
          compliant and offers a Data Processing Addendum for handling
          personal data. Audit reports and compliance certifications are
          available on request through their Trust Center.
        </li>
        <li>
          <strong>The application layer (<Wordmark size="inherit" />).</strong>{' '}
          Our own code, access controls, and the confidentiality
          commitments described on this page. <Wordmark size="inherit" />{' '}
          does not currently hold its own separate SOC 2 attestation; we
          rely on MindStudio&apos;s audited infrastructure and our own
          documented practices. If you are an institutional buyer who
          needs a vendor security review, reach out and we will work
          through it with you.
        </li>
      </ul>

      <h2>What we do — and don&apos;t do — with your data</h2>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, margin: '12px 0 24px' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid var(--brass)', textAlign: 'left' }}>
            <th style={{ padding: '10px 12px', fontWeight: 600 }}>What happens with your invention details</th>
            <th style={{ padding: '10px 12px', fontWeight: 600 }}>Yes / No</th>
          </tr>
        </thead>
        <tbody>
          <tr style={{ borderBottom: '1px solid var(--border-hairline)' }}>
            <td style={{ padding: '10px 12px' }}>Encrypted in transit (TLS)</td>
            <td style={{ padding: '10px 12px', color: '#1B5E2F', fontWeight: 600 }}>Yes</td>
          </tr>
          <tr style={{ borderBottom: '1px solid var(--border-hairline)' }}>
            <td style={{ padding: '10px 12px' }}>Encrypted at rest</td>
            <td style={{ padding: '10px 12px', color: '#1B5E2F', fontWeight: 600 }}>Yes</td>
          </tr>
          <tr style={{ borderBottom: '1px solid var(--border-hairline)' }}>
            <td style={{ padding: '10px 12px' }}>Access logged and monitored</td>
            <td style={{ padding: '10px 12px', color: '#1B5E2F', fontWeight: 600 }}>Yes</td>
          </tr>
          <tr style={{ borderBottom: '1px solid var(--border-hairline)' }}>
            <td style={{ padding: '10px 12px' }}>Deletable on request</td>
            <td style={{ padding: '10px 12px', color: '#1B5E2F', fontWeight: 600 }}>Yes</td>
          </tr>
          <tr style={{ borderBottom: '1px solid var(--border-hairline)' }}>
            <td style={{ padding: '10px 12px' }}>Used to train AI models</td>
            <td style={{ padding: '10px 12px', color: 'var(--oxblood, #8B2E2E)', fontWeight: 600 }}>No</td>
          </tr>
          <tr style={{ borderBottom: '1px solid var(--border-hairline)' }}>
            <td style={{ padding: '10px 12px' }}>Sold or shared with third parties</td>
            <td style={{ padding: '10px 12px', color: 'var(--oxblood, #8B2E2E)', fontWeight: 600 }}>No</td>
          </tr>
          <tr style={{ borderBottom: '1px solid var(--border-hairline)' }}>
            <td style={{ padding: '10px 12px' }}>Given to advertising or analytics tools</td>
            <td style={{ padding: '10px 12px', color: 'var(--oxblood, #8B2E2E)', fontWeight: 600 }}>No</td>
          </tr>
          <tr style={{ borderBottom: '1px solid var(--border-hairline)' }}>
            <td style={{ padding: '10px 12px' }}>Published anywhere public</td>
            <td style={{ padding: '10px 12px', color: 'var(--oxblood, #8B2E2E)', fontWeight: 600 }}>No</td>
          </tr>
          <tr>
            <td style={{ padding: '10px 12px' }}>Available to other users / search engines</td>
            <td style={{ padding: '10px 12px', color: 'var(--oxblood, #8B2E2E)', fontWeight: 600 }}>No</td>
          </tr>
        </tbody>
      </table>

      <p>
        One thing the table above doesn&apos;t cover: research. To check your
        invention against what already exists, <Wordmark size="inherit" /> sends
        search terms drawn from it (short technical and category terms, not your
        full description) to public patent databases like Google Patents and the
        USPTO, to academic and literature indexes, and to web search, then reads
        what comes back. This is how the prior-art search and the market research
        in your memos stay backed by real, cited sources. Your invention itself
        is never posted, filed, published, or handed to anyone. Only the search
        terms needed to look it up ever leave the platform.
      </p>

      <h2>Where your data lives, and who processes it</h2>
      <p>
        Servers are based in the United States, hosted on Amazon Web
        Services. The platform&apos;s infrastructure subprocessors,
        published in full on the{' '}
        <a
          href="https://trust.mindstudio.ai/subprocessors"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'var(--brass-deep)' }}
        >
          MindStudio Trust Center
        </a>
        , include <strong>Amazon Web Services</strong> (US),{' '}
        <strong>Cloudflare</strong> (content delivery),{' '}
        <strong>Vercel</strong> (US), <strong>Zilliz</strong> (US), and{' '}
        <strong>Google Cloud Platform</strong> (US).
      </p>
      <p>
        Because <Wordmark size="inherit" /> uses AI to analyze your
        invention, your inputs are processed by the{' '}
        <strong>AI model providers</strong> we route to: primarily{' '}
        <strong>Anthropic</strong> (the Claude models), with{' '}
        <strong>OpenAI</strong> and <strong>Google</strong> models for
        specific functions. MindStudio&apos;s broader platform offers
        additional AI providers, but <Wordmark size="inherit" /> does not
        route your invention data to them. Every provider we use operates
        under enterprise API terms that prohibit using your inputs to
        train their models, and your inputs are not retained beyond what
        is needed to return your result.
      </p>

      <h2>Active security practices</h2>
      <p>
        The following controls operate at the infrastructure layer,
        maintained and independently audited as part of MindStudio&apos;s
        SOC 2 program:
      </p>
      <ul>
        <li><strong>Penetration testing</strong> — performed regularly by qualified third-party security firms.</li>
        <li><strong>Disaster recovery plans tested</strong> — not just documented but actively exercised.</li>
        <li><strong>Cybersecurity insurance maintained</strong> — financial backstop in the unlikely event of an incident.</li>
        <li><strong>Encryption key access restricted</strong> — only authorized personnel can access encryption keys.</li>
        <li><strong>Asset disposal procedures</strong> — secure data destruction at end-of-life for any hardware.</li>
      </ul>

      <h2>How <Wordmark size="inherit" /> differs from ChatGPT or Claude</h2>
      <p>
        Several testers asked us this directly, so we want to answer it
        plainly. The short version: ChatGPT, Claude, and general AI
        chatbots are wonderful tools, but they were not built with
        invention confidentiality in mind.
      </p>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, margin: '12px 0 24px' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid var(--brass)', textAlign: 'left' }}>
            <th style={{ padding: '10px 12px', fontWeight: 600 }}></th>
            <th style={{ padding: '10px 12px', fontWeight: 600 }}>General AI chatbots</th>
            <th style={{ padding: '10px 12px', fontWeight: 600 }}><Wordmark size="inherit" /></th>
          </tr>
        </thead>
        <tbody>
          <tr style={{ borderBottom: '1px solid var(--border-hairline)' }}>
            <td style={{ padding: '10px 12px', fontWeight: 500 }}>Confidentiality contract</td>
            <td style={{ padding: '10px 12px', color: 'var(--text-decorative)' }}>Consumer terms vary; inputs may be reviewed by humans</td>
            <td style={{ padding: '10px 12px' }}>Explicit Confidentiality Terms accepted before any sensitive interaction</td>
          </tr>
          <tr style={{ borderBottom: '1px solid var(--border-hairline)' }}>
            <td style={{ padding: '10px 12px', fontWeight: 500 }}>Used to train AI models</td>
            <td style={{ padding: '10px 12px', color: 'var(--text-decorative)' }}>Sometimes opt-out, sometimes default-on</td>
            <td style={{ padding: '10px 12px' }}>Never — confirmed by infrastructure provider</td>
          </tr>
          <tr style={{ borderBottom: '1px solid var(--border-hairline)' }}>
            <td style={{ padding: '10px 12px', fontWeight: 500 }}>Patent-specific workflow</td>
            <td style={{ padding: '10px 12px', color: 'var(--text-decorative)' }}>General-purpose conversation</td>
            <td style={{ padding: '10px 12px' }}>Structured strategy session built with input from patent attorneys</td>
          </tr>
          <tr style={{ borderBottom: '1px solid var(--border-hairline)' }}>
            <td style={{ padding: '10px 12px', fontWeight: 500 }}>Prior-art search</td>
            <td style={{ padding: '10px 12px', color: 'var(--text-decorative)' }}>Hallucinates patent numbers</td>
            <td style={{ padding: '10px 12px' }}>Live search of US patent databases</td>
          </tr>
          <tr>
            <td style={{ padding: '10px 12px', fontWeight: 500 }}>Structured deliverable</td>
            <td style={{ padding: '10px 12px', color: 'var(--text-decorative)' }}>Free-form conversation</td>
            <td style={{ padding: '10px 12px' }}>Invention strategy memo: verdict, prior art, filing path, next steps</td>
          </tr>
        </tbody>
      </table>

      <h2>You&apos;re in control</h2>
      <p>
        Every piece of data you generate while using{' '}
        <Wordmark size="inherit" /> is yours. In{' '}
        <Link href="/settings/privacy" style={{ color: 'var(--brass-deep)' }}>
          Settings → Privacy
        </Link>{' '}
        you can:
      </p>
      <ul>
        <li>Export everything you&apos;ve created (ideas, memos, sessions) as a downloadable file</li>
        <li>Delete any individual idea — and every memo, session, and version tied to it</li>
        <li>Delete your entire account — and every piece of data attached to it</li>
      </ul>
      <p>
        We do not keep "shadow copies" or backups beyond the standard
        infrastructure retention window required for disaster recovery.
        Deleted data is gone.
      </p>

      <h2>Frequently asked</h2>
      <p>
        <strong>Does using <Wordmark size="inherit" /> count as &quot;public disclosure&quot; of my invention?</strong>
      </p>
      <p>
        No. <Wordmark size="inherit" /> operates as a confidential
        analysis tool, not a public forum. Specifically:
      </p>
      <ul>
        <li>Your inputs are encrypted in transit and at rest, stored on SOC 2 Type II-audited infrastructure.</li>
        <li>Your inputs are <strong>never</strong> used to train AI models — confirmed by MindStudio&apos;s Trust Center.</li>
        <li>Only authorized personnel have access to your data, and all access is logged.</li>
        <li>By using <Wordmark size="inherit" />, you accept our Confidentiality Terms, which contractually bind us to keep your inputs confidential.</li>
      </ul>
      <p>
        Under U.S. patent law (35 U.S.C. § 102), a &quot;public disclosure&quot;
        generally means making an invention available to the public in
        a way that puts the relevant field on notice. Using a
        confidential, encrypted tool that does not share or publish
        your inputs is materially different from posting your idea on a
        public forum, blog, or trade show.
      </p>
      <p>
        <em>
          This page is not legal advice. For binding determinations
          about your specific situation, consult a registered patent
          attorney.
        </em>
      </p>

      <p>
        <strong>What happens if <Wordmark size="inherit" /> is acquired or shuts down?</strong>
      </p>
      <p>
        Your data remains yours. In the event of acquisition or wind-down,
        you will receive notice and an export window to download
        everything you&apos;ve created before any change to access. We
        will never sell or transfer your invention data without your
        explicit consent.
      </p>

      <p>
        <strong>What happens if there is a security incident?</strong>
      </p>
      <p>
        If any incident affects your data, you will be notified directly,
        and we follow breach-notification timelines aligned with GDPR
        (within 72 hours of becoming aware) and applicable state laws.
      </p>

      <p>
        <strong>How do I report a security concern?</strong>
      </p>
      <p>
        Email <a href="mailto:security@entredash.com" style={{ color: 'var(--brass-deep)' }}>security@entredash.com</a> — we read every message and respond within one business day.
      </p>

      <h2>Independent verification</h2>
      <p>
        Don&apos;t take our word for it. Every claim on this page is
        independently verifiable:
      </p>
      <ul>
        <li>
          <a
            href="https://trust.mindstudio.ai/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--brass-deep)' }}
          >
            MindStudio Trust Center
          </a>{' '}
          — full audit reports, control disclosures, subprocessor list
        </li>
        <li>
          <Link href="/terms" style={{ color: 'var(--brass-deep)' }}>Terms of Service</Link>{' '}
          — including Confidentiality Terms
        </li>
        <li>
          <Link href="/privacy" style={{ color: 'var(--brass-deep)' }}>Privacy Policy</Link>{' '}
          — what we collect, what we do with it
        </li>
      </ul>
    </>
  );
}

export function SecurityPage() {
  return (
    <LegalShell title="Trust & Security">
      <SecurityBody />
    </LegalShell>
  );
}

/**
 * Tiny brand-mark stub used in the public footer. Re-exporting Wordmark so
 * the footer doesn't reach back into components.tsx for it directly.
 */
export { Wordmark };
